import React, { useState } from 'react';
import { Box, TextField, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SendIcon from '@material-ui/icons/Send';
import { format } from 'date-fns';
import { MDTypography } from '../../../components/MDTypography';
import { TaskDetail, FeedType } from '../../../types/interfaces/task.types';
import Typography from '@mui/material/Typography';
import { useAuthContext } from 'contexts/authContext';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    height: '91vh',
    display: 'flex',
    flexDirection: 'column',
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  feedContainer: {
    flex: 1,
    overflowY: 'auto',
    marginBottom: theme.spacing(2),
  },
  feedEntry: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: '#d6e9f1',
    borderRadius: theme.shape.borderRadius,
  },
  myFeedEntry: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: '#d6f1d8',
    borderRadius: theme.shape.borderRadius,
  },
  feedHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
  },
  feedAuthor: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: '#707070ff',
  },
  feedTime: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  feedMessage: {
    fontSize: 14,
    fontWeight: 400,
  },
  commentBox: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: 'auto',
  },
}));

interface TaskFeedProps {
  task: TaskDetail;
  currentActor: 'FAIRFARE' | 'CARRIER' | 'NETWORK' | null | undefined;
  addComment: (message: string, type: FeedType) => Promise<boolean>;
}

const TaskFeed: React.FC<TaskFeedProps> = ({
  task,
  currentActor,
  addComment,
}) => {
  const classes = useStyles();
  const [comment, setComment] = useState('');
  const { user } = useAuthContext();

  const handleAddComment = async () => {
    if (!comment.trim()) return;

    const success = await addComment(comment.trim(), FeedType.TEXT);
    if (success) {
      setComment('');
    }
  };

  const renderFeedEntry = (entry: any) => {
    const isSystemEvent = entry.type === FeedType.SYSTEM;
    const isMyComment = entry.metadata.createdBy === currentActor;

    return (
      <Box
        key={entry.id}
        className={isMyComment ? classes.myFeedEntry : classes.feedEntry}
      >
        <Box className={classes.feedHeader}>
          <MDTypography
            variant="caption"
            style={{ color: '#707070ff' }}
            className={classes.feedAuthor}
          >
            {isSystemEvent
              ? 'System'
              : `[${entry.metadata.createdBy}] ${
                  entry.createdBy?.fullName || user?.fullName || ''
                }`}
          </MDTypography>
          <MDTypography
            variant="caption"
            style={{ color: '#707070ff' }}
            className={classes.feedTime}
          >
            {format(new Date(entry.createdAt), 'MMM dd, yyyy HH:mm')}
          </MDTypography>
        </Box>
        {entry.message && (
          <Typography
            style={{ fontSize: 14, fontWeight: 400 }}
            className={classes.feedMessage}
          >
            {entry.message}
          </Typography>
        )}
        {entry.attachmentUrl && (
          <Typography className={classes.feedMessage}>
            Attachment:{' '}
            <a
              href={entry.attachmentUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {entry.attachmentName || 'Download'}
            </a>
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box className={classes.root}>
      <Box flexShrink={0}>
        <MDTypography className={classes.sectionTitle}>
          Activity Feed
        </MDTypography>
      </Box>

      <Box
        className={classes.feedContainer}
        style={{ minHeight: 0, padding: '8px 0' }}
      >
        {task.feed && task.feed.length > 0 ? (
          [...task.feed].reverse().map(renderFeedEntry)
        ) : (
          <MDTypography
            variant="body2"
            style={{ color: 'rgba(0, 0, 0, 0.54)' }}
            align="center"
          >
            No activity yet
          </MDTypography>
        )}
      </Box>

      <Box className={classes.commentBox} flexShrink={0}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Add a comment..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onKeyUp={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleAddComment();
            }
          }}
        />
        <IconButton
          color="primary"
          onClick={handleAddComment}
          disabled={!comment.trim()}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default TaskFeed;
